import { useState, useEffect, useCallback, useMemo, Fragment, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useIsCraneHire } from '../hooks/useCurrentIndustry';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../components/ui/sheet';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { SortableTableHead } from '../components/ui/sortable-table-head';
import { useServerSort } from '../hooks/useSortableTable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { GooglePlacesAutocomplete } from '../components/ui/google-places-autocomplete';
import { ConfirmationDialog } from '../components/ui/confirmation-dialog';
import useLocale from '../hooks/useLocale';
import { getStatesForCountry, getCountryByCode } from '../utils/countryStates';
import { useInfiniteList } from '../hooks/useInfiniteList';
import { AsyncCustomerPicker } from '../components/AsyncCustomerPicker';
import { 
  Plus, Pencil, Trash2, Building2, Search, Phone, Mail, User, MoreHorizontal, 
  DollarSign, Users, Star, Ban, ChevronDown, ChevronUp, MapPin, Shield, ExternalLink, Link2, UserCheck, UserPlus, ArchiveRestore, Archive 
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const Customers = () => {
  const { api, user } = useAuth();
  const isCraneHire = useIsCraneHire();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'customers');
  const [customerStatusFilter, setCustomerStatusFilter] = useState(
    searchParams.get('status') || 'active'
  );
  const [tenantCountry, setTenantCountry] = useState('AU');
  const [systemFuelLevy, setSystemFuelLevy] = useState(null);
  
  // Customer state
  const [customers, setCustomers] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);

  // Auto-open Add Customer dialog when navigated with state.openDialog
  useEffect(() => {
    if (location.state?.openDialog) {
      setEditingCustomer(null);
      setDialogOpen(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const [xeroExporting, setXeroExporting] = useState(null); // Track which customer is being exported
  const [myobExporting, setMyobExporting] = useState(null); // Track which customer is being exported to MYOB
  const [qboExporting, setQboExporting] = useState(null); // Track which customer is being exported to QuickBooks
  
  // Tenant accounting connection status
  const [accountingConnection, setAccountingConnection] = useState({
    xero_connected: false,
    myob_connected: false,
    qbo_connected: false
  });
  const [tenantLocale, setTenantLocale] = useState('en-AU');
  const t = useLocale(tenantLocale);
  
  const [customerForm, setCustomerForm] = useState({
    customer_type: 'organisation',
    company_name: '',
    contact_name: '',
    phone: '',
    email: '',
    docket_to_email: '',
    disable_auto_docket_email: false,
    price_list_id: '',
    notes: '',
    requires_po: false,
    accounting_connection_id: '',
    sync_disabled: false,
    tower_crane_billing_period: '',
    required_compliance_type_ids: [],
    preferred_compliance_type_ids: []
  });
  const [acctConnections, setAcctConnections] = useState([]);

  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({
    customer_id: '',
    name: '',
    phone: '',
    email: '',
    position: '',
    is_primary: false
  });
  
  // Customer drawer tab state
  const [customerDrawerTab, setCustomerDrawerTab] = useState('details');
  const [drawerKey, setDrawerKey] = useState(0);

  // Sites state
  const [complianceTypes, setComplianceTypes] = useState([]);
  const [allowanceTypes, setAllowanceTypes] = useState([]);
  const [siteSearchTerm, setSiteSearchTerm] = useState('');

  // Infinite-scroll lists (server-side paginated + searched). EW2-305.
  const custSort = useServerSort();
  const siteSort = useServerSort();
  const custList = useInfiniteList({
    endpoint: '/customers/paginated',
    params: { status: customerStatusFilter, ...custSort.sortParams },
    search: searchTerm,
  });
  const [siteStatusFilter, setSiteStatusFilter] = useState('active');
  const siteList = useInfiniteList({ endpoint: '/sites/paginated', search: siteSearchTerm, params: { ...siteSort.sortParams, status: siteStatusFilter } });
  const sites = siteList.items; // alias for legacy references / maps
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [siteForm, setSiteForm] = useState({
    name: '',
    street_address: '',
    suburb: '',
    postcode: '',
    state: '',
    address: '',
    customer_id: '',
    notes: '',
    required_compliance_type_ids: [],
    preferred_compliance_type_ids: [],
    allowance_rates: []
  });

  // Site contacts state
  const [siteContacts, setSiteContacts] = useState([]);
  const [availableCustomerContacts, setAvailableCustomerContacts] = useState([]);
  const [siteSheetTab, setSiteSheetTab] = useState('details');
  const [siteContactDialogOpen, setSiteContactDialogOpen] = useState(false);
  const [linkContactDialogOpen, setLinkContactDialogOpen] = useState(false);
  const [editingSiteContact, setEditingSiteContact] = useState(null);
  const [siteContactForm, setSiteContactForm] = useState({
    name: '',
    job_title: '',
    phone: '',
    email: '',
    is_primary: false,
    notes: ''
  });

  // Operator Preferences state
  const [preferencesDialogOpen, setPreferencesDialogOpen] = useState(false);
  const [selectedCustomerForPrefs, setSelectedCustomerForPrefs] = useState(null);
  const [customerPreferences, setCustomerPreferences] = useState([]);
  const [prefForm, setPrefForm] = useState({
    operator_id: '',
    preference_type: 'preferred',
    priority: 1,
    notes: ''
  });
  const [editingPreference, setEditingPreference] = useState(null);
  const [expandedCustomerId, setExpandedCustomerId] = useState(null);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const deletePendingRef = useRef(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(null); // 'customer', 'contact', 'site', 'site_contact', 'operator_pref'
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch contacts for a customer
  const fetchContacts = useCallback(async (customerId) => {
    try {
      const response = await api.get(`/customers/${customerId}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  }, [api]);

  // Update URL when tab changes
  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      const [priceRes, opRes, compRes, allowRes] = await Promise.all([
        api.get('/price-lists/active'),
        api.get('/operators'),
        api.get('/compliance-types'),
        api.get('/allowance-types').catch(() => ({ data: [] }))
      ]);
      setPriceLists(priceRes.data);
      setOperators(opRes.data);
      setComplianceTypes(compRes.data);
      setAllowanceTypes(allowRes.data || []);
      try {
        const connRes = await api.get('/accounting/connections');
        setAcctConnections(connRes.data.connections || []);
      } catch (e) { /* non-fatal */ }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Refresh aux data (dropdowns/name-map) AND both paginated lists after a mutation.
  const refreshAll = useCallback(() => {
    fetchData();
    custList.reload();
    siteList.reload();
  }, [fetchData, custList.reload, siteList.reload]);

  // Fetch tenant accounting connection status
  const fetchAccountingConnection = useCallback(async () => {
    if (!user?.client_id) return;
    try {
      const response = await api.get(`/tenants/${user.client_id}`);
      setAccountingConnection({
        xero_connected: response.data.xero_connected || false,
        myob_connected: response.data.myob_connected || false,
        qbo_connected: response.data.qbo_connected || false
      });
      if (response.data.locale) setTenantLocale(response.data.locale);
    } catch (error) {
      console.error('Failed to fetch accounting connection status');
    }
  }, [api, user?.client_id]);

  useEffect(() => {
    fetchData();
    fetchAccountingConnection();
    // Fetch tenant country for address autocomplete
    api.get('/tenants/settings').then(res => {
      setTenantCountry(res.data.country || 'AU');
      setSystemFuelLevy(res.data.fuel_levy_percentage || null);
    }).catch(() => {});
  }, [fetchData, fetchAccountingConnection]);

  const statesList = getStatesForCountry(tenantCountry);
  const countryMeta = getCountryByCode(tenantCountry);

  // ==================== CUSTOMER HANDLERS ====================
  const resetCustomerForm = () => {
    setCustomerForm({
      customer_type: 'organisation',
      company_name: '',
      contact_name: '',
      phone: '',
      email: '',
      docket_to_email: '',
      disable_auto_docket_email: false,
      price_list_id: '',
      fuel_levy_percentage: '',
      notes: '',
      requires_po: false,
      accounting_connection_id: '',
      sync_disabled: false,
    tower_crane_billing_period: '',
      required_compliance_type_ids: [],
      preferred_compliance_type_ids: []
    });
    setEditingCustomer(null);
  };

  const handleOpenCustomerDialog = (customer = null, tab = 'details') => {
    if (customer) {
      setEditingCustomer(customer);
      setCustomerForm({
        customer_type: customer.customer_type || 'organisation',
        company_name: customer.company_name || '',
        contact_name: customer.contact_name,
        phone: customer.phone || '',
        email: customer.email || '',
        docket_to_email: customer.docket_to_email || '',
        disable_auto_docket_email: customer.disable_auto_docket_email || false,
        price_list_id: customer.price_list_id || '',
        fuel_levy_percentage: customer.fuel_levy_percentage != null ? customer.fuel_levy_percentage : '',
        notes: customer.notes || '',
        requires_po: customer.requires_po || false,
        accounting_connection_id: customer.accounting_connection_id || '',
        sync_disabled: customer.sync_disabled || false,
        tower_crane_billing_period: customer.tower_crane_billing_period || '',
        required_compliance_type_ids: customer.required_compliance_type_ids || [],
        preferred_compliance_type_ids: customer.preferred_compliance_type_ids || []
      });
      // Fetch contacts for this customer
      fetchContacts(customer.id);
      // Reset contact form
      setEditingContact(null);
      setContactForm({
        customer_id: customer.id,
        name: '',
        phone: '',
        email: '',
        position: '',
        is_primary: false
      });
    } else {
      resetCustomerForm();
      setContacts([]);
    }
    // Set tab and increment key to force Tabs remount
    setCustomerDrawerTab(tab);
    setDrawerKey(k => k + 1);
    setDialogOpen(true);
  };

  const handleCustomerSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...customerForm };
      if (!submitData.price_list_id) submitData.price_list_id = null;
      if (!submitData.accounting_connection_id) submitData.accounting_connection_id = null;
      // Fuel levy: empty string = null (use system default), number = customer override
      submitData.fuel_levy_percentage = submitData.fuel_levy_percentage === '' || submitData.fuel_levy_percentage === null
        ? null
        : parseFloat(submitData.fuel_levy_percentage);
      
      if (editingCustomer) {
        await api.put(`/customers/${editingCustomer.id}`, submitData);
        toast.success('Customer updated');
      } else {
        await api.post('/customers', submitData);
        toast.success('Customer added');
      }
      setDialogOpen(false);
      resetCustomerForm();
      refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  // Contact handlers
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!editingCustomer) return;
    
    try {
      if (editingContact) {
        await api.put(`/contacts/${editingContact.id}`, contactForm);
        toast.success('Contact updated');
      } else {
        await api.post(`/customers/${editingCustomer.id}/contacts`, contactForm);
        toast.success('Contact added');
      }
      setEditingContact(null);
      setContactForm({
        customer_id: editingCustomer.id,
        name: '',
        phone: '',
        email: '',
        position: '',
        is_primary: false
      });
      fetchContacts(editingCustomer.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDeleteContact = async (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    setItemToDelete({ ...contact, customerId: editingCustomer?.id });
    setDeleteType('contact');
    setDeleteDialogOpen(true);
  };

  const handleDeleteCustomer = async (id) => {
    const customer = custList.items.find(c => c.id === id);
    setItemToDelete(customer);
    setDeleteType('customer');
    setDeleteDialogOpen(true);
  };

  // Unified delete confirmation handler
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      switch (deleteType) {
        case 'customer':
          await api.delete(`/customers/${itemToDelete.id}`);
          toast.success('Customer archived');
          refreshAll();
          break;
        case 'contact':
          await api.delete(`/contacts/${itemToDelete.id}`);
          toast.success('Contact deleted');
          if (itemToDelete.customerId) {
            fetchContacts(itemToDelete.customerId);
          }
          break;
        case 'site':
          await api.delete(`/sites/${itemToDelete.id}`);
          toast.success('Site archived');
          refreshAll();
          break;
        case 'site_contact':
          await api.delete(`/site-contacts/${itemToDelete.id}`);
          toast.success('Contact removed from site');
          if (itemToDelete.siteId) {
            await fetchSiteContacts(itemToDelete.siteId);
            await fetchAvailableCustomerContacts(itemToDelete.customerId, itemToDelete.siteId);
          }
          break;
        case 'operator_pref':
          await api.delete(`/customers/${itemToDelete.customerId}/operator-preferences/${itemToDelete.id}`);
          toast.success('Preference removed');
          await fetchCustomerPreferences(itemToDelete.customerId);
          break;
        default:
          break;
      }
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      // Surface the 409 blocked-reasons payload from the archive endpoint
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.reasons) {
        const r = detail.reasons;
        const parts = [];
        if (r.open_jobs) parts.push(`${r.open_jobs} open job${r.open_jobs > 1 ? 's' : ''}`);
        if (r.open_dockets) parts.push(`${r.open_dockets} open docket${r.open_dockets > 1 ? 's' : ''}`);
        if (r.open_dry_hire_invoices) parts.push(`${r.open_dry_hire_invoices} draft dry-hire invoice${r.open_dry_hire_invoices > 1 ? 's' : ''}`);
        const entity = deleteType === 'site' ? 'site' : 'customer';
        toast.error(`Cannot archive ${entity}: ${parts.join(', ')}. Close these records first.`);
      } else {
        toast.error((typeof detail === 'string' && detail) || 'Action failed');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Restore (unarchive) a customer
  const handleRestoreCustomer = async (customer) => {
    try {
      await api.post(`/customers/${customer.id}/restore`);
      toast.success('Customer restored');
      refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Restore failed');
    }
  };

  // Get delete dialog info based on delete type
  const getDeleteDialogInfo = () => {
    switch (deleteType) {
      case 'customer':
        return {
          title: 'Archive Customer',
          description: `Archive "${itemToDelete?.company_name}"? Archived customers are hidden from pickers and dashboards but all historical jobs, dockets, and invoices are preserved. You can restore them later from the Archived filter. Archive is blocked if the customer has open jobs, dockets, or draft dry-hire invoices.`
        };
      case 'contact':
        return {
          title: 'Delete Contact',
          description: `Are you sure you want to delete "${itemToDelete?.name}"? This action cannot be undone.`
        };
      case 'site':
        return {
          title: 'Archive Site',
          description: `Are you sure you want to archive "${itemToDelete?.name}"? It will be hidden from pickers but historical records will remain intact.`
        };
      case 'site_contact':
        return {
          title: 'Remove Site Contact',
          description: `Remove "${itemToDelete?.name}" from this site?`
        };
      case 'operator_pref':
        return {
          title: 'Remove Operator Preference',
          description: `Remove this operator preference?`
        };
      default:
        return { title: 'Delete', description: 'Are you sure?' };
    }
  };

  // Send customer to Xero
  const handleSendToXero = async (customer) => {
    try {
      setXeroExporting(customer.id);
      const response = await api.post(`/xero/export-customer/${customer.id}`);
      if (response.data.status === 'linked') {
        toast.success('Customer linked to existing Xero contact');
      } else {
        toast.success('Customer exported to Xero');
      }
      refreshAll(); // Refresh to show Xero badge
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send to Xero');
    } finally {
      setXeroExporting(null);
    }
  };

  const handleSendToMyob = async (customer) => {
    try {
      setMyobExporting(customer.id);
      const response = await api.post(`/myob/export-customer/${customer.id}`);
      if (response.data.status === 'linked') {
        toast.success('Customer linked to existing MYOB contact');
      } else {
        toast.success('Customer exported to MYOB');
      }
      refreshAll(); // Refresh to show MYOB badge
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send to MYOB');
    } finally {
      setMyobExporting(null);
    }
  };

  const handleSendToQbo = async (customer) => {
    try {
      setQboExporting(customer.id);
      const response = await api.post(`/quickbooks/export-customer/${customer.id}`);
      if (response.data.status === 'linked') {
        toast.success('Customer linked to existing QuickBooks customer');
      } else {
        toast.success('Customer exported to QuickBooks');
      }
      refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send to QuickBooks');
    } finally {
      setQboExporting(null);
    }
  };

  const getPriceListName = (id) => {
    const pl = priceLists.find(p => p.id === id);
    return pl?.name || '-';
  };

  // ================== MEMOIZED LOOKUP MAPS ==================
  // These improve performance for repeated lookups
  const customersMap = useMemo(() => new Map(customers.map(c => [c.id, c])), [customers]);
  const sitesMap = useMemo(() => new Map(sites.map(s => [s.id, s])), [sites]);
  const complianceTypesMap = useMemo(() => new Map(complianceTypes.map(c => [c.id, c])), [complianceTypes]);

  // ==================== SITE HANDLERS ====================
  const resetSiteForm = () => {
    setSiteForm({
      name: '',
      street_address: '',
      suburb: '',
      postcode: '',
      state: '',
      address: '',
      customer_id: '',
      notes: '',
      required_compliance_type_ids: [],
      preferred_compliance_type_ids: [],
      allowance_rates: []
    });
    setEditingSite(null);
  };

  const handleOpenSiteDialog = async (site = null) => {
    if (site) {
      setEditingSite(site);
      setSiteForm({
        name: site.name,
        street_address: site.street_address || '',
        suburb: site.suburb || '',
        postcode: site.postcode || '',
        state: site.state || '',
        address: site.address || '',
        customer_id: site.customer_id,
        notes: site.notes || '',
        required_compliance_type_ids: site.required_compliance_type_ids || [],
        preferred_compliance_type_ids: site.preferred_compliance_type_ids || [],
        allowance_rates: site.allowance_rates || []
      });
      setSiteSheetTab('details');
      // Fetch site contacts
      await fetchSiteContacts(site.id);
      await fetchAvailableCustomerContacts(site.customer_id, site.id);
    } else {
      resetSiteForm();
      setSiteContacts([]);
      setAvailableCustomerContacts([]);
      setSiteSheetTab('details');
    }
    setSiteDialogOpen(true);
  };

  const toggleCustomerComplianceType = (typeId, listType) => {
    const currentList = customerForm[listType];
    const otherList = listType === 'required_compliance_type_ids'
      ? 'preferred_compliance_type_ids'
      : 'required_compliance_type_ids';
    if (currentList.includes(typeId)) {
      setCustomerForm({ ...customerForm, [listType]: currentList.filter(id => id !== typeId) });
    } else {
      setCustomerForm({
        ...customerForm,
        [listType]: [...currentList, typeId],
        [otherList]: customerForm[otherList].filter(id => id !== typeId)
      });
    }
  };

  const toggleComplianceType = (typeId, listType) => {
    const currentList = siteForm[listType];
    const otherList = listType === 'required_compliance_type_ids' 
      ? 'preferred_compliance_type_ids' 
      : 'required_compliance_type_ids';
    
    if (currentList.includes(typeId)) {
      setSiteForm({ ...siteForm, [listType]: currentList.filter(id => id !== typeId) });
    } else {
      setSiteForm({ 
        ...siteForm, 
        [listType]: [...currentList, typeId],
        [otherList]: siteForm[otherList].filter(id => id !== typeId)
      });
    }
  };

  const handleSiteSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSite) {
        await api.put(`/sites/${editingSite.id}`, siteForm);
        toast.success('Site updated');
      } else {
        await api.post('/sites', siteForm);
        toast.success('Site added');
      }
      setSiteDialogOpen(false);
      resetSiteForm();
      refreshAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDeleteSite = async (id) => {
    deletePendingRef.current = true;
    const site = sites.find(s => s.id === id);
    setItemToDelete(site);
    setDeleteType('site');
    setDeleteDialogOpen(true);
  };

  // Use memoized map for O(1) lookup
  const getCustomerName = useCallback((customerId) => {
    const customer = customersMap.get(customerId);
    return customer?.company_name || 'Unknown';
  }, [customersMap]);

  const getComplianceTypeName = useCallback((typeId) => {
    const ct = complianceTypesMap.get(typeId);
    return ct?.name || 'Unknown';
  }, [complianceTypesMap]);

  const getSiteCountForCustomer = useCallback((customer) => {
    // Server-provided per-customer count (paginated endpoint), avoids loading all sites.
    return customer?.site_count ?? 0;
  }, []);

  // ==================== SITE CONTACTS HANDLERS ====================
  const fetchSiteContacts = useCallback(async (siteId) => {
    try {
      const response = await api.get(`/sites/${siteId}/contacts`);
      setSiteContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch site contacts:', error);
      setSiteContacts([]);
    }
  }, [api]);

  const fetchAvailableCustomerContacts = useCallback(async (customerId, siteId) => {
    try {
      const response = await api.get(`/customers/${customerId}/contacts/available-for-site/${siteId}`);
      setAvailableCustomerContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch available contacts:', error);
      setAvailableCustomerContacts([]);
    }
  }, [api]);

  const resetSiteContactForm = () => {
    setSiteContactForm({
      name: '',
      job_title: '',
      phone: '',
      email: '',
      is_primary: false,
      notes: ''
    });
    setEditingSiteContact(null);
  };

  const handleOpenSiteContactDialog = (contact = null) => {
    if (contact) {
      setEditingSiteContact(contact);
      setSiteContactForm({
        name: contact.name,
        job_title: contact.job_title || '',
        phone: contact.phone || '',
        email: contact.email || '',
        is_primary: contact.is_primary || false,
        notes: contact.notes || ''
      });
    } else {
      resetSiteContactForm();
    }
    setSiteContactDialogOpen(true);
  };

  const handleSiteContactSubmit = async (e) => {
    e.preventDefault();
    if (!editingSite) return;

    try {
      const submitData = {
        ...siteContactForm,
        site_id: editingSite.id
      };

      if (editingSiteContact) {
        await api.put(`/site-contacts/${editingSiteContact.id}`, submitData);
        toast.success('Contact updated');
      } else {
        await api.post(`/sites/${editingSite.id}/contacts`, submitData);
        toast.success('Contact added');
      }
      setSiteContactDialogOpen(false);
      resetSiteContactForm();
      await fetchSiteContacts(editingSite.id);
      await fetchAvailableCustomerContacts(editingSite.customer_id, editingSite.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleLinkCustomerContact = async (customerContactId, isPrimary = false) => {
    if (!editingSite) return;

    try {
      await api.post(`/sites/${editingSite.id}/contacts/link`, {
        customer_contact_id: customerContactId,
        is_primary: isPrimary
      });
      toast.success('Contact linked to site');
      setLinkContactDialogOpen(false);
      await fetchSiteContacts(editingSite.id);
      await fetchAvailableCustomerContacts(editingSite.customer_id, editingSite.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link contact');
    }
  };

  const handleDeleteSiteContact = async (contactId) => {
    const contact = siteContacts.find(c => c.id === contactId);
    setItemToDelete({ ...contact, siteId: editingSite?.id, customerId: editingSite?.customer_id });
    setDeleteType('site_contact');
    setDeleteDialogOpen(true);
  };

  // ==================== OPERATOR PREFERENCES HANDLERS ====================
  const fetchCustomerPreferences = async (customerId) => {
    try {
      const res = await api.get(`/customers/${customerId}/operator-preferences`);
      setCustomerPreferences(res.data);
    } catch (error) {
      setCustomerPreferences([]);
    }
  };

  const handleOpenPreferencesDialog = async (customer) => {
    setSelectedCustomerForPrefs(customer);
    await fetchCustomerPreferences(customer.id);
    resetPrefForm();
    setPreferencesDialogOpen(true);
  };

  const resetPrefForm = () => {
    setPrefForm({
      operator_id: '',
      preference_type: 'preferred',
      priority: 1,
      notes: ''
    });
    setEditingPreference(null);
  };

  const handleEditPreference = (pref) => {
    setEditingPreference(pref);
    setPrefForm({
      operator_id: pref.operator_id,
      preference_type: pref.preference_type,
      priority: pref.priority || 1,
      notes: pref.notes || ''
    });
  };

  const handleSubmitPreference = async (e) => {
    e.preventDefault();
    if (!selectedCustomerForPrefs) return;

    try {
      const submitData = {
        ...prefForm,
        customer_id: selectedCustomerForPrefs.id,
        priority: prefForm.preference_type === 'preferred' ? parseInt(prefForm.priority) : null
      };

      if (editingPreference) {
        await api.put(`/customers/${selectedCustomerForPrefs.id}/operator-preferences/${editingPreference.id}`, submitData);
        toast.success('Preference updated');
      } else {
        await api.post(`/customers/${selectedCustomerForPrefs.id}/operator-preferences`, submitData);
        toast.success('Preference added');
      }
      await fetchCustomerPreferences(selectedCustomerForPrefs.id);
      resetPrefForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save preference');
    }
  };

  const handleDeletePreference = async (prefId) => {
    if (!selectedCustomerForPrefs) return;
    const pref = customerPreferences.find(p => p.id === prefId);
    setItemToDelete({ ...pref, customerId: selectedCustomerForPrefs.id });
    setDeleteType('operator_pref');
    setDeleteDialogOpen(true);
  };

  const toggleExpandCustomer = async (customerId) => {
    if (expandedCustomerId === customerId) {
      setExpandedCustomerId(null);
    } else {
      setExpandedCustomerId(customerId);
      await fetchCustomerPreferences(customerId);
    }
  };

  const getOperatorName = (operatorId) => {
    const op = operators.find(o => o.id === operatorId);
    return op?.name || 'Unknown';
  };

  // Server-side search + pagination via useInfiniteList (EW2-305).
  const filteredCustomers = custList.items;
  const filteredSites = siteList.items;

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Customers & Sites</h1>
          <p className="text-muted-foreground">Manage your customers and their job sites</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card 
          className="card-industrial cursor-pointer hover:border-[#ffd108]/50 transition-colors"
          onClick={() => handleTabChange('customers')}
          data-testid="stat-card-customers"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#ffd108]/20 rounded-lg">
                <Building2 className="h-5 w-5 text-[#ffd108]" />
              </div>
              <div>
                <p className="text-2xl font-bold">{custList.total}</p>
                <p className="text-xs text-muted-foreground">Customers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="card-industrial cursor-pointer hover:border-blue-500/50 transition-colors"
          onClick={() => handleTabChange('sites')}
          data-testid="stat-card-sites"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <MapPin className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{siteList.total}</p>
                <p className="text-xs text-muted-foreground">Sites</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="card-industrial cursor-pointer hover:border-green-500/50 transition-colors"
          onClick={() => navigate('/data-management?tab=price-lists')}
          data-testid="stat-card-pricelists"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{priceLists.length}</p>
                <p className="text-xs text-muted-foreground">Price Lists</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="customers" data-testid="tab-customers">
            <Building2 className="h-4 w-4 mr-2" />
            Customers ({custList.total})
          </TabsTrigger>
          <TabsTrigger value="sites" data-testid="tab-sites">
            <MapPin className="h-4 w-4 mr-2" />
            Sites ({siteList.total})
          </TabsTrigger>
        </TabsList>

        {/* Customers Tab */}
        <TabsContent value="customers" className="mt-6">
          <Card className="card-industrial">
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex flex-col sm:flex-row gap-2 sm:items-center flex-1">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="customer-search"
                      placeholder="Search customers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select
                    value={customerStatusFilter}
                    onValueChange={(v) => {
                      setCustomerStatusFilter(v);
                      const next = new URLSearchParams(searchParams);
                      next.set('tab', 'customers');
                      if (v === 'active') {
                        next.delete('status');
                      } else {
                        next.set('status', v);
                      }
                      setSearchParams(next);
                    }}
                  >
                    <SelectTrigger className="w-full sm:w-[160px]" data-testid="customer-status-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active" data-testid="customer-status-active">Active</SelectItem>
                      <SelectItem value="archived" data-testid="customer-status-archived">Archived</SelectItem>
                      <SelectItem value="all" data-testid="customer-status-all">All</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button className="btn-volt" onClick={() => handleOpenCustomerDialog()} data-testid="add-customer-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Customer
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="label-overline w-[30px]"></TableHead>
                      <SortableTableHead sortKey="company" requestSort={custSort.requestSort} getSortDirection={custSort.getSortDirection} className="label-overline">Company</SortableTableHead>
                      <SortableTableHead sortKey="phone" requestSort={custSort.requestSort} getSortDirection={custSort.getSortDirection} className="label-overline">Phone</SortableTableHead>
                      <SortableTableHead sortKey="email" requestSort={custSort.requestSort} getSortDirection={custSort.getSortDirection} className="label-overline">Email</SortableTableHead>
                      <TableHead className="label-overline">Contacts</TableHead>
                      <TableHead className="label-overline">Sites</TableHead>
                      <TableHead className="label-overline">Price List</TableHead>
                      <TableHead className="label-overline text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCustomers.map((customer) => (
                      <Fragment key={customer.id}>
                        <TableRow 
                          data-testid={`customer-row-${customer.id}`}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={(e) => {
                            // Don't trigger if clicking the expand button or actions menu
                            if (e.target.closest('button')) return;
                            handleOpenCustomerDialog(customer);
                          }}
                        >
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => toggleExpandCustomer(customer.id)}
                            >
                              {expandedCustomerId === customer.id ? 
                                <ChevronUp className="h-4 w-4" /> : 
                                <ChevronDown className="h-4 w-4" />
                              }
                            </Button>
                          </TableCell>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {customer.customer_type === 'individual' ? (
                                <User className="h-4 w-4 text-blue-500" />
                              ) : (
                                <Building2 className="h-4 w-4 text-[#ffd108]" />
                              )}
                              {customer.company_name}
                              {customer.customer_type === 'individual' && (
                                <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0 h-4 border-blue-500 text-blue-500">
                                  Individual
                                </Badge>
                              )}
                              {customer.xero_contact_id && (
                                <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0 h-4 border-[#13B5EA] text-[#13B5EA]">
                                  <Link2 className="h-2.5 w-2.5 mr-0.5" />
                                  Xero
                                </Badge>
                              )}
                              {customer.myob_customer_id && (
                                <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0 h-4 border-[#6B21A8] text-[#6B21A8]">
                                  <Link2 className="h-2.5 w-2.5 mr-0.5" />
                                  MYOB
                                </Badge>
                              )}
                              {customer.qbo_customer_id && (
                                <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0 h-4 border-[#2CA01C] text-[#2CA01C]">
                                  <Link2 className="h-2.5 w-2.5 mr-0.5" />
                                  QB
                                </Badge>
                              )}
                              {customer.archived && (
                                <Badge
                                  variant="outline"
                                  className="ml-1 text-[10px] px-1 py-0 h-4 border-muted-foreground/40 text-muted-foreground bg-muted/30"
                                  data-testid={`archived-badge-${customer.id}`}
                                >
                                  Archived
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {customer.phone && (
                              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Phone className="h-3 w-3" />
                                {customer.phone}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {customer.email && (
                              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Mail className="h-3 w-3" />
                                {customer.email}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              <User className="h-3 w-3 mr-1" />
                              {customer.contact_count || 0}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              <Badge variant="secondary">
                                <MapPin className="h-3 w-3 mr-1" />
                                {getSiteCountForCustomer(customer)}
                              </Badge>
                              {customer.requires_po && (
                                <Badge variant="outline" className="border-amber-300 text-amber-700">
                                  PO Required
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {customer.price_list_id && (
                              <Badge variant="outline">
                                <DollarSign className="h-3 w-3 mr-1" />
                                {getPriceListName(customer.price_list_id)}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`customer-actions-${customer.id}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenCustomerDialog(customer, 'details'); }}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Edit Details
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenCustomerDialog(customer, 'contacts'); }}>
                                  <User className="h-4 w-4 mr-2" />
                                  Contacts
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenPreferencesDialog(customer); }}>
                                  <Users className="h-4 w-4 mr-2" />
                                  Operator Preferences
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={(e) => {
                                  e.stopPropagation();
                                  setSiteForm({ ...siteForm, customer_id: customer.id });
                                  setSiteDialogOpen(true);
                                }}>
                                  <MapPin className="h-4 w-4 mr-2" />
                                  Add Site
                                </DropdownMenuItem>
                                {accountingConnection.xero_connected && !customer.xero_contact_id && (
                                  <DropdownMenuItem 
                                    onClick={(e) => { e.stopPropagation(); handleSendToXero(customer); }}
                                    disabled={xeroExporting === customer.id}
                                  >
                                    {xeroExporting === customer.id ? (
                                      <>
                                        <span className="h-4 w-4 mr-2 animate-spin">⏳</span>
                                        Sending...
                                      </>
                                    ) : (
                                      <>
                                        <ExternalLink className="h-4 w-4 mr-2 text-[#13B5EA]" />
                                        Send to Xero
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                )}
                                {accountingConnection.myob_connected && !customer.myob_customer_id && (
                                  <DropdownMenuItem 
                                    onClick={(e) => { e.stopPropagation(); handleSendToMyob(customer); }}
                                    disabled={myobExporting === customer.id}
                                  >
                                    {myobExporting === customer.id ? (
                                      <>
                                        <span className="h-4 w-4 mr-2 animate-spin">⏳</span>
                                        Sending...
                                      </>
                                    ) : (
                                      <>
                                        <ExternalLink className="h-4 w-4 mr-2 text-[#6B21A8]" />
                                        Send to MYOB
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                )}
                                {accountingConnection.qbo_connected && !customer.qbo_customer_id && (
                                  <DropdownMenuItem 
                                    onClick={(e) => { e.stopPropagation(); handleSendToQbo(customer); }}
                                    disabled={qboExporting === customer.id}
                                  >
                                    {qboExporting === customer.id ? (
                                      <>
                                        <span className="h-4 w-4 mr-2 animate-spin">⏳</span>
                                        Sending...
                                      </>
                                    ) : (
                                      <>
                                        <ExternalLink className="h-4 w-4 mr-2 text-green-600" />
                                        Send to QuickBooks
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                {customer.archived ? (
                                  <DropdownMenuItem 
                                    onClick={(e) => { e.stopPropagation(); handleRestoreCustomer(customer); }}
                                    data-testid={`restore-customer-btn-${customer.id}`}
                                  >
                                    <ArchiveRestore className="h-4 w-4 mr-2" />
                                    Restore
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem 
                                    onClick={(e) => { e.stopPropagation(); handleDeleteCustomer(customer.id); }}
                                    className="text-destructive"
                                    data-testid={`archive-customer-btn-${customer.id}`}
                                  >
                                    <Archive className="h-4 w-4 mr-2" />
                                    Archive
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                        {/* Expanded preferences row */}
                        {expandedCustomerId === customer.id && (
                          <TableRow className="bg-muted/30">
                            <TableCell colSpan={8} className="py-4">
                              <div className="pl-8">
                                <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                  <Users className="h-4 w-4" />
                                  Operator Preferences
                                </h4>
                                {customerPreferences.length === 0 ? (
                                  <p className="text-sm text-muted-foreground">No operator preferences set</p>
                                ) : (
                                  <div className="flex flex-wrap gap-2">
                                    {customerPreferences.map(pref => (
                                      <Badge
                                        key={pref.id}
                                        variant={pref.preference_type === 'preferred' ? 'default' : 'destructive'}
                                        className="flex items-center gap-1"
                                      >
                                        {pref.preference_type === 'preferred' ? (
                                          <Star className="h-3 w-3" />
                                        ) : (
                                          <Ban className="h-3 w-3" />
                                        )}
                                        {getOperatorName(pref.operator_id)}
                                        {pref.preference_type === 'preferred' && pref.priority && (
                                          <span className="text-xs opacity-75">#{pref.priority}</span>
                                        )}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="mt-2"
                                  onClick={() => handleOpenPreferencesDialog(customer)}
                                >
                                  <Pencil className="h-3 w-3 mr-1" />
                                  Manage Preferences
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
                {filteredCustomers.length === 0 && !custList.loading && (
                  <div className="text-center py-8 text-muted-foreground">
                    No customers found
                  </div>
                )}
                {/* Infinite-scroll sentinel + count footer (EW2-305) */}
                <div ref={custList.sentinelRef} />
                <div className="sticky bottom-0 flex items-center justify-center py-3 text-sm text-muted-foreground bg-background/95 backdrop-blur border-t" data-testid="customers-scroll-footer">
                  {custList.loading || custList.loadingMore ? (
                    <span className="flex items-center gap-2"><span className="h-4 w-4 border-2 border-muted-foreground/40 border-t-transparent rounded-full animate-spin" /> Loading…</span>
                  ) : filteredCustomers.length > 0 ? (
                    <div className="flex items-center gap-3">
                      <span>Showing {filteredCustomers.length} of {custList.total}</span>
                      {custList.hasMore && (
                        <Button size="sm" variant="outline" onClick={custList.loadMore} data-testid="customers-load-more">Load more</Button>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sites Tab */}
        <TabsContent value="sites" className="mt-6">
          <Card className="card-industrial">
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      data-testid="site-search"
                      placeholder="Search sites..."
                      value={siteSearchTerm}
                      onChange={(e) => setSiteSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select value={siteStatusFilter} onValueChange={setSiteStatusFilter}>
                    <SelectTrigger className="w-[130px] h-9" data-testid="site-status-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                      <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button className="btn-volt" onClick={() => handleOpenSiteDialog()} data-testid="add-site-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Site
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableTableHead sortKey="name" requestSort={siteSort.requestSort} getSortDirection={siteSort.getSortDirection} className="label-overline">Site Name</SortableTableHead>
                      <TableHead className="label-overline">Customer</TableHead>
                      <SortableTableHead sortKey="address" requestSort={siteSort.requestSort} getSortDirection={siteSort.getSortDirection} className="label-overline">Address</SortableTableHead>
                      <TableHead className="label-overline">Compliance</TableHead>
                      <TableHead className="label-overline text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSites.map((site) => (
                      <TableRow 
                        key={site.id} 
                        data-testid={`site-row-${site.id}`}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={(e) => {
                          // Don't trigger if clicking actions menu or delete is pending
                          if (e.target.closest('button') || e.target.closest('[role="menuitem"]') || deletePendingRef.current) return;
                          handleOpenSiteDialog(site);
                        }}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-[#ffd108]" />
                            {site.name || [site.street_address, site.suburb].filter(Boolean).join(', ') || 'Unnamed Site'}
                            {site.archived && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Archived</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="flex items-center gap-1 text-sm">
                            <Building2 className="h-3 w-3 text-muted-foreground" />
                            {site.customer_name}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-muted-foreground">
                            {site.street_address && <div>{site.street_address}</div>}
                            <div>
                              {[site.suburb, site.state, site.postcode].filter(Boolean).join(', ')}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {site.required_compliance_type_ids?.map(id => (
                              <Badge key={id} variant="destructive" className="text-xs">
                                <Shield className="h-3 w-3 mr-1" />
                                {getComplianceTypeName(id)}
                              </Badge>
                            ))}
                            {site.preferred_compliance_type_ids?.map(id => (
                              <Badge key={id} variant="secondary" className="text-xs">
                                <Star className="h-3 w-3 mr-1" />
                                {getComplianceTypeName(id)}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`site-actions-${site.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" onCloseAutoFocus={(e) => { if (deletePendingRef.current) e.preventDefault(); }}>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenSiteDialog(site); }}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {site.archived ? (
                                <DropdownMenuItem 
                                  onClick={async (e) => { e.stopPropagation(); try { await api.post(`/sites/${site.id}/restore`); toast.success('Site restored'); siteList.reload(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed to restore site'); } }}
                                  className="text-green-500"
                                >
                                  <ArchiveRestore className="h-4 w-4 mr-2" />
                                  Restore
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem 
                                  onClick={(e) => { e.stopPropagation(); handleDeleteSite(site.id); }}
                                  className="text-destructive"
                                >
                                  <Archive className="h-4 w-4 mr-2" />
                                  Archive
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {filteredSites.length === 0 && !siteList.loading && (
                  <div className="text-center py-8 text-muted-foreground">
                    No sites found
                  </div>
                )}
                {/* Infinite-scroll sentinel + count footer (EW2-305) */}
                <div ref={siteList.sentinelRef} />
                <div className="sticky bottom-0 flex items-center justify-center py-3 text-sm text-muted-foreground bg-background/95 backdrop-blur border-t" data-testid="sites-scroll-footer">
                  {siteList.loading || siteList.loadingMore ? (
                    <span className="flex items-center gap-2"><span className="h-4 w-4 border-2 border-muted-foreground/40 border-t-transparent rounded-full animate-spin" /> Loading…</span>
                  ) : filteredSites.length > 0 ? (
                    <div className="flex items-center gap-3">
                      <span>Showing {filteredSites.length} of {siteList.total}</span>
                      {siteList.hasMore && (
                        <Button size="sm" variant="outline" onClick={siteList.loadMore} data-testid="sites-load-more">Load more</Button>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Operator Preferences Dialog */}
      <Dialog open={preferencesDialogOpen} onOpenChange={setPreferencesDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Users className="h-5 w-5" />
              Operator Preferences - {selectedCustomerForPrefs?.company_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Current Preferences */}
            {customerPreferences.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Current Preferences</Label>
                <div className="border rounded-lg divide-y">
                  {customerPreferences.map(pref => (
                    <div key={pref.id} className="flex items-center justify-between p-2">
                      <div className="flex items-center gap-2">
                        <Badge variant={pref.preference_type === 'preferred' ? 'default' : 'destructive'}>
                          {pref.preference_type === 'preferred' ? (
                            <Star className="h-3 w-3 mr-1" />
                          ) : (
                            <Ban className="h-3 w-3 mr-1" />
                          )}
                          {pref.preference_type}
                        </Badge>
                        <span className="font-medium">{getOperatorName(pref.operator_id)}</span>
                        {pref.preference_type === 'preferred' && pref.priority && (
                          <span className="text-xs text-muted-foreground">Priority #{pref.priority}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleEditPreference(pref)}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDeletePreference(pref.id)}>
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add/Edit Form */}
            <form onSubmit={handleSubmitPreference} className="space-y-3 border-t pt-4">
              <div className="space-y-2">
                <Label className="label-overline">
                  {editingPreference ? 'Edit Preference' : 'Add Preference'}
                </Label>
                <Select 
                  value={prefForm.operator_id} 
                  onValueChange={(v) => setPrefForm({ ...prefForm, operator_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select operator" />
                  </SelectTrigger>
                  <SelectContent>
                    {operators.map(op => (
                      <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="label-overline">Type</Label>
                  <Select 
                    value={prefForm.preference_type} 
                    onValueChange={(v) => setPrefForm({ ...prefForm, preference_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="preferred">
                        <span className="flex items-center gap-1">
                          <Star className="h-3 w-3 text-green-500" />
                          Preferred
                        </span>
                      </SelectItem>
                      <SelectItem value="blocked">
                        <span className="flex items-center gap-1">
                          <Ban className="h-3 w-3 text-red-500" />
                          Blocked
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {prefForm.preference_type === 'preferred' && (
                  <div className="space-y-2">
                    <Label className="label-overline">Priority</Label>
                    <Select 
                      value={prefForm.priority.toString()} 
                      onValueChange={(v) => setPrefForm({ ...prefForm, priority: parseInt(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5].map(p => (
                          <SelectItem key={p} value={p.toString()}>#{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {editingPreference && (
                  <Button type="button" variant="outline" onClick={resetPrefForm}>
                    Cancel
                  </Button>
                )}
                <Button type="submit" className="btn-volt" disabled={!prefForm.operator_id}>
                  {editingPreference ? 'Update' : 'Add'} Preference
                </Button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Sheet (Flyout Panel) - Combined with Contacts */}
      <Sheet open={dialogOpen} onOpenChange={setDialogOpen}>
        <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl">
              {editingCustomer ? editingCustomer.company_name : 'Add New Customer'}
            </SheetTitle>
          </SheetHeader>
          
          <Tabs key={drawerKey} defaultValue={customerDrawerTab} onValueChange={setCustomerDrawerTab} className="mt-4" data-testid="customer-form">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Details
              </TabsTrigger>
              <TabsTrigger value="contacts" className="flex items-center gap-2" disabled={!editingCustomer}>
                <User className="h-4 w-4" />
                Contacts {editingCustomer && contacts.length > 0 && `(${contacts.length})`}
              </TabsTrigger>
            </TabsList>
            
            {/* Details Tab */}
            <TabsContent value="details" className="mt-4">
              <form onSubmit={handleCustomerSubmit} className="space-y-4">
                {/* Customer Type Toggle */}
                <div className="space-y-2">
                  <Label className="label-overline">Customer Type <span className="text-red-500">*</span></Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={customerForm.customer_type === 'organisation' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCustomerForm({ ...customerForm, customer_type: 'organisation' })}
                      className="flex-1"
                      data-testid="customer-type-organisation"
                    >
                      <Building2 className="h-4 w-4 mr-2" />
                      Organisation
                    </Button>
                    <Button
                      type="button"
                      variant={customerForm.customer_type === 'individual' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCustomerForm({ ...customerForm, customer_type: 'individual' })}
                      className="flex-1"
                      data-testid="customer-type-individual"
                    >
                      <User className="h-4 w-4 mr-2" />
                      Individual
                    </Button>
                  </div>
                </div>
                
                {/* Company Name - Only show for organisations */}
                {customerForm.customer_type === 'organisation' && (
                  <div className="space-y-2">
                    <Label className="label-overline">Company Name <span className="text-red-500">*</span></Label>
                    <Input
                      data-testid="customer-company-input"
                      value={customerForm.company_name}
                      onChange={(e) => setCustomerForm({ ...customerForm, company_name: e.target.value })}
                      placeholder="ABC Construction"
                      required
                    />
                  </div>
                )}
                
                {/* Name field - Only show for individuals */}
                {customerForm.customer_type === 'individual' && (
                  <div className="space-y-2">
                    <Label className="label-overline">Name <span className="text-red-500">*</span></Label>
                    <Input
                      data-testid="customer-contact-input"
                      value={customerForm.contact_name}
                      onChange={(e) => setCustomerForm({ ...customerForm, contact_name: e.target.value })}
                      placeholder="John Smith"
                      required
                    />
                  </div>
                )}
                
                {/* Business Phone & Email */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="label-overline">
                      Phone
                    </Label>
                    <Input
                      data-testid="customer-phone-input"
                      value={customerForm.phone}
                      onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                      placeholder="+1 234 567 890"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">
                      Email
                    </Label>
                    <Input
                      data-testid="customer-email-input"
                      type="email"
                      value={customerForm.email}
                      onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                      placeholder="info@company.com"
                    />
                  </div>
                </div>

                {/* Docket auto-email settings */}
                <div className="space-y-3 p-4 bg-muted/30 rounded-lg border">
                  <div className="space-y-2">
                    <Label className="label-overline flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Docket To Email
                    </Label>
                    <Input
                      data-testid="customer-docket-to-email-input"
                      type="email"
                      value={customerForm.docket_to_email}
                      onChange={(e) => setCustomerForm({ ...customerForm, docket_to_email: e.target.value })}
                      placeholder="dockets@company.com"
                    />
                    <p className="text-xs text-muted-foreground">
                      Where this customer receives docket PDFs when auto-email on mobile submit is enabled. Site contacts selected on the job are also emailed.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="disable-auto-docket-email"
                      data-testid="customer-disable-auto-docket-email"
                      checked={customerForm.disable_auto_docket_email}
                      onCheckedChange={(checked) => setCustomerForm({ ...customerForm, disable_auto_docket_email: checked })}
                    />
                    <Label htmlFor="disable-auto-docket-email" className="text-sm font-normal cursor-pointer">
                      Don't auto-send dockets to this customer
                    </Label>
                  </div>
                </div>
                
                {/* Primary Contact Section - Only for organisations */}
                {customerForm.customer_type === 'organisation' && editingCustomer && (
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg border">
                    <div className="flex items-center justify-between">
                      <Label className="label-overline flex items-center gap-2">
                        <UserCheck className="h-4 w-4" />
                        Primary Contact
                      </Label>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setCustomerDrawerTab('contacts')}
                        className="text-xs"
                      >
                        Manage Contacts
                      </Button>
                    </div>
                    {(() => {
                      const primaryContact = contacts.find(c => c.is_primary);
                      if (primaryContact) {
                        return (
                          <div className="space-y-1.5">
                            <p className="font-medium text-sm">{primaryContact.name}</p>
                            {primaryContact.position && (
                              <p className="text-xs text-muted-foreground">{primaryContact.position}</p>
                            )}
                            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                              {primaryContact.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {primaryContact.phone}
                                </span>
                              )}
                              {primaryContact.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {primaryContact.email}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      } else {
                        return (
                          <p className="text-sm text-muted-foreground italic">
                            No primary contact set. Go to Contacts tab to add and set a primary contact.
                          </p>
                        );
                      }
                    })()}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label className="label-overline">Price List</Label>
                  <Select 
                    value={customerForm.price_list_id} 
                    onValueChange={(v) => setCustomerForm({ ...customerForm, price_list_id: v })}
                  >
                    <SelectTrigger data-testid="customer-pricelist-select">
                      <SelectValue placeholder="Select price list (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {priceLists.map(pl => (
                        <SelectItem key={pl.id} value={pl.id}>{pl.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2" data-testid="customer-fuel-levy">
                  <Label className="label-overline">Fuel Levy %</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      placeholder={`System default${systemFuelLevy ? ` (${systemFuelLevy}%)` : ''}`}
                      value={customerForm.fuel_levy_percentage}
                      onChange={(e) => setCustomerForm({ ...customerForm, fuel_levy_percentage: e.target.value })}
                      data-testid="customer-fuel-levy-input"
                    />
                    {customerForm.fuel_levy_percentage !== '' && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setCustomerForm({ ...customerForm, fuel_levy_percentage: '' })}
                        className="text-xs whitespace-nowrap"
                        data-testid="customer-fuel-levy-clear"
                      >
                        Use default
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {customerForm.fuel_levy_percentage === '' 
                      ? 'Using system default. Set a value to override for this customer, or 0 to disable.'
                      : customerForm.fuel_levy_percentage === '0' || customerForm.fuel_levy_percentage === 0
                        ? 'Fuel levy disabled for this customer.'
                        : `Custom rate: ${customerForm.fuel_levy_percentage}% (overrides system default).`
                    }
                  </p>
                </div>
                {acctConnections.length > 0 && (
                  <div className="space-y-2" data-testid="customer-accounting-system">
                    <Label className="label-overline">Accounting System</Label>
                    <Select
                      value={customerForm.sync_disabled ? '__none__' : (customerForm.accounting_connection_id || '__unset__')}
                      onValueChange={(v) => {
                        if (v === '__none__') setCustomerForm({ ...customerForm, sync_disabled: true, accounting_connection_id: '' });
                        else if (v === '__unset__') setCustomerForm({ ...customerForm, sync_disabled: false, accounting_connection_id: '' });
                        else setCustomerForm({ ...customerForm, sync_disabled: false, accounting_connection_id: v });
                      }}
                    >
                      <SelectTrigger data-testid="customer-accounting-select">
                        <SelectValue placeholder="Select accounting system" />
                      </SelectTrigger>
                      <SelectContent>
                        {acctConnections.length > 1 && (
                          <SelectItem value="__unset__" data-testid="accounting-unassigned">Not assigned (awaiting link)</SelectItem>
                        )}
                        {acctConnections.map((c) => (
                          <SelectItem key={c.id} value={c.id} data-testid={`accounting-conn-${c.id}`}>
                            {c.label} ({c.provider}){c.is_default ? ' — default' : ''}
                          </SelectItem>
                        ))}
                        <SelectItem value="__none__" data-testid="accounting-dont-sync">Don't sync this customer</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Where this customer's invoices export. "Don't sync" keeps them out of all accounting systems.</p>
                    {editingCustomer && customerForm.accounting_connection_id && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-500/10 h-7 px-2"
                        data-testid="unlink-customer-accounting"
                        onClick={async () => {
                          try {
                            await api.post(`/customers/${editingCustomer.id}/accounting-link`, { unlink: true });
                            toast.success('Customer unlinked from accounting');
                            setCustomerForm({ ...customerForm, accounting_connection_id: '', sync_disabled: false });
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Failed to unlink');
                          }
                        }}
                      >
                        Unlink from accounting
                      </Button>
                    )}
                  </div>
                )}
                <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                  <Checkbox
                    id="requires-po"
                    checked={customerForm.requires_po}
                    onCheckedChange={(checked) => setCustomerForm({ ...customerForm, requires_po: checked })}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="requires-po" className="text-sm font-medium cursor-pointer">
                      Requires PO Number
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Jobs for this customer will require a mandatory Purchase Order number
                    </p>
                  </div>
                </div>
                {isCraneHire && (
                  <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
                    <Label className="label-overline">Tower Crane Billing Period</Label>
                    <select
                      className="w-full px-2 py-1.5 text-sm rounded-md border bg-background"
                      value={customerForm.tower_crane_billing_period || ''}
                      onChange={e => setCustomerForm({ ...customerForm, tower_crane_billing_period: e.target.value || null })}
                      data-testid="customer-tc-billing-period"
                    >
                      <option value="">Not set (use job default)</option>
                      <option value="weekly">Weekly</option>
                      <option value="fortnightly">Fortnightly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                    <p className="text-xs text-muted-foreground">
                      Default billing period for tower crane jobs created for this customer. Can be overridden per job.
                    </p>
                  </div>
                )}
                {complianceTypes.length > 0 && (
                  <div className="space-y-4 p-3 rounded-lg border border-border/60">
                    <p className="text-xs text-muted-foreground -mb-2">
                      Compliance an operator needs to be assigned to this customer's jobs. Applied in addition to any site requirements.
                    </p>
                    <div className="space-y-2">
                      <Label className="label-overline flex items-center gap-2">
                        <Shield className="h-4 w-4 text-red-500" />
                        Required Compliance (mandatory)
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Operators must hold these (not expired) — allocation is blocked otherwise
                      </p>
                      <div className="flex flex-wrap gap-2 p-2 border rounded-md max-h-32 overflow-y-auto" data-testid="customer-required-compliance">
                        {complianceTypes.filter(ct => ct.is_active).map(ct => (
                          <Badge
                            key={ct.id}
                            data-testid={`customer-required-compliance-${ct.id}`}
                            variant={customerForm.required_compliance_type_ids.includes(ct.id) ? 'destructive' : 'outline'}
                            className="cursor-pointer"
                            onClick={() => toggleCustomerComplianceType(ct.id, 'required_compliance_type_ids')}
                          >
                            {customerForm.required_compliance_type_ids.includes(ct.id) && (
                              <Shield className="h-3 w-3 mr-1" />
                            )}
                            {ct.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline flex items-center gap-2">
                        <Star className="h-4 w-4 text-yellow-500" />
                        Preferred Compliance
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Prefer operators with these (warning only — allocation still allowed)
                      </p>
                      <div className="flex flex-wrap gap-2 p-2 border rounded-md max-h-32 overflow-y-auto" data-testid="customer-preferred-compliance">
                        {complianceTypes.filter(ct => ct.is_active).map(ct => (
                          <Badge
                            key={ct.id}
                            data-testid={`customer-preferred-compliance-${ct.id}`}
                            variant={customerForm.preferred_compliance_type_ids.includes(ct.id) ? 'default' : 'outline'}
                            className="cursor-pointer"
                            onClick={() => toggleCustomerComplianceType(ct.id, 'preferred_compliance_type_ids')}
                          >
                            {customerForm.preferred_compliance_type_ids.includes(ct.id) && (
                              <Star className="h-3 w-3 mr-1" />
                            )}
                            {ct.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="label-overline">Notes</Label>
                  <Textarea
                    data-testid="customer-notes-input"
                    value={customerForm.notes}
                    onChange={(e) => setCustomerForm({ ...customerForm, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={3}
                  />
                </div>
                <div className="flex gap-2 pt-4 border-t mt-6">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="btn-volt" data-testid="save-customer-btn">
                    {editingCustomer ? 'Update' : 'Add Customer'}
                  </Button>
                </div>
              </form>
            </TabsContent>
            
            {/* Contacts Tab */}
            <TabsContent value="contacts" className="mt-4">
              <div className="space-y-6">
                {/* Existing Contacts */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Contacts ({contacts.length})
                  </h3>
                  {contacts.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center bg-muted/50 rounded-lg">
                      No contacts added yet
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {contacts.map(contact => (
                        <div key={contact.id} className={`flex items-center justify-between p-3 rounded-lg ${contact.is_primary ? 'bg-primary/10 border border-primary/30' : 'bg-muted/50'}`}>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{contact.name}</span>
                              {contact.is_primary && (
                                <Badge className="text-xs bg-primary text-primary-foreground">
                                  <UserCheck className="h-3 w-3 mr-1" />
                                  Primary
                                </Badge>
                              )}
                              {contact.position && (
                                <Badge variant="outline" className="text-xs">{contact.position}</Badge>
                              )}
                            </div>
                            <div className="flex gap-4 text-sm text-muted-foreground mt-1">
                              {contact.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {contact.phone}
                                </span>
                              )}
                              {contact.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {contact.email}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setEditingContact(contact);
                                setContactForm({
                                  customer_id: editingCustomer.id,
                                  name: contact.name,
                                  phone: contact.phone || '',
                                  email: contact.email || '',
                                  position: contact.position || '',
                                  is_primary: contact.is_primary || false
                                });
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteContact(contact.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Add/Edit Contact Form */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">
                    {editingContact ? 'Edit Contact' : 'Add New Contact'}
                  </h3>
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="label-overline">Name <span className="text-red-500">*</span></Label>
                        <Input
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          placeholder="Contact name"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="label-overline">Position</Label>
                        <Input
                          value={contactForm.position}
                          onChange={(e) => setContactForm({ ...contactForm, position: e.target.value })}
                          placeholder="e.g. Site Manager, Accounts"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="label-overline">Phone</Label>
                        <Input
                          value={contactForm.phone}
                          onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                          placeholder="+61 400 000 000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="label-overline">Email</Label>
                        <Input
                          type="email"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          placeholder="contact@company.com"
                        />
                      </div>
                    </div>
                    {/* Primary Contact Toggle */}
                    <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                      <Checkbox
                        id="contact-is-primary"
                        checked={contactForm.is_primary}
                        onCheckedChange={(checked) => setContactForm({ ...contactForm, is_primary: checked })}
                        data-testid="contact-is-primary-checkbox"
                      />
                      <div className="space-y-1">
                        <Label htmlFor="contact-is-primary" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                          <UserCheck className="h-4 w-4" />
                          Set as Primary Contact
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Primary contact will be shown on customer details and used for communications
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {editingContact && (
                        <Button 
                          type="button" 
                          variant="outline"
                          onClick={() => {
                            setEditingContact(null);
                            setContactForm({
                              customer_id: editingCustomer?.id || '',
                              name: '',
                              phone: '',
                              email: '',
                              position: '',
                              is_primary: false
                            });
                          }}
                        >
                          Cancel Edit
                        </Button>
                      )}
                      <Button type="submit" className="btn-volt">
                        {editingContact ? 'Update Contact' : 'Add Contact'}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
              
              <div className="flex gap-2 pt-4 border-t mt-6">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Close</Button>
              </div>
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      {/* Site Sheet (Flyout Panel) */}
      <Sheet open={siteDialogOpen} onOpenChange={setSiteDialogOpen}>
        <SheetContent side="right" className="w-[800px] sm:max-w-[800px] overflow-y-auto">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading text-xl flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#ffd108]" />
              {editingSite ? editingSite.name : 'Add New Site'}
            </SheetTitle>
          </SheetHeader>
          
          {editingSite ? (
            <Tabs value={siteSheetTab} onValueChange={setSiteSheetTab} className="mt-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="contacts" className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Contacts ({siteContacts.length})
                </TabsTrigger>
              </TabsList>

              {/* Details Tab */}
              <TabsContent value="details" className="mt-4">
                <form onSubmit={handleSiteSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="label-overline">Customer <span className="text-red-500">*</span></Label>
                    <AsyncCustomerPicker
                      value={siteForm.customer_id}
                      selectedName={editingSite?.customer_name}
                      onChange={(id) => setSiteForm({ ...siteForm, customer_id: id })}
                      testId="site-customer-select"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">Search Address</Label>
                    <GooglePlacesAutocomplete
                      placeholder="Start typing an address..."
                      country={tenantCountry}
                      onPlaceSelect={(place) => {
                        setSiteForm(prev => ({
                          ...prev,
                          name: [place.street_address, place.suburb].filter(Boolean).join(', ') || place.name || prev.name,
                          street_address: place.street_address || '',
                          suburb: place.suburb || '',
                          state: place.state || '',
                          postcode: place.postcode || ''
                        }));
                      }}
                      data-testid="site-address-autocomplete"
                    />
                    <p className="text-xs text-muted-foreground">Or enter address manually below</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">Site Name</Label>
                    <Input
                      data-testid="site-name-input"
                      value={siteForm.name}
                      onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })}
                      placeholder="e.g. 17 Highview Cres, Abuntha"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">Street Address</Label>
                    <Input
                      data-testid="site-street-input"
                      value={siteForm.street_address}
                      onChange={(e) => setSiteForm({ ...siteForm, street_address: e.target.value })}
                      placeholder="123 Main Street"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="label-overline">{t('suburb')}</Label>
                      <Input
                        data-testid="site-suburb-input"
                        value={siteForm.suburb}
                        onChange={(e) => setSiteForm({ ...siteForm, suburb: e.target.value })}
                        placeholder={t('suburb')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">{t('postcode')}</Label>
                      <Input
                        data-testid="site-postcode-input"
                        value={siteForm.postcode}
                        onChange={(e) => setSiteForm({ ...siteForm, postcode: e.target.value })}
                        placeholder="2000"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">{t('state')}</Label>
                    <Select 
                      value={siteForm.state} 
                      onValueChange={(v) => setSiteForm({ ...siteForm, state: v })}
                    >
                      <SelectTrigger data-testid="site-state-select">
                        <SelectValue placeholder={`Select ${countryMeta?.stateLabel?.toLowerCase() || 'state'}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {statesList.map(s => (
                          <SelectItem key={s.code} value={s.code}>{s.code}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {complianceTypes.length > 0 && (
                    <>
                      <div className="space-y-2">
                        <Label className="label-overline flex items-center gap-2">
                          <Shield className="h-4 w-4 text-red-500" />
                          Required Compliance
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Operators must have these certifications to work on this site
                        </p>
                        <div className="flex flex-wrap gap-2 p-2 border rounded-md max-h-32 overflow-y-auto">
                          {complianceTypes.filter(ct => ct.is_active).map(ct => (
                            <Badge
                              key={ct.id}
                              variant={siteForm.required_compliance_type_ids.includes(ct.id) ? 'destructive' : 'outline'}
                              className="cursor-pointer"
                              onClick={() => toggleComplianceType(ct.id, 'required_compliance_type_ids')}
                            >
                              {siteForm.required_compliance_type_ids.includes(ct.id) && (
                                <Shield className="h-3 w-3 mr-1" />
                              )}
                              {ct.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="label-overline flex items-center gap-2">
                          <Shield className="h-4 w-4 text-yellow-500" />
                          Preferred Compliance
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Prefer operators with these certifications (not mandatory)
                        </p>
                        <div className="flex flex-wrap gap-2 p-2 border rounded-md max-h-32 overflow-y-auto">
                          {complianceTypes.filter(ct => ct.is_active).map(ct => (
                            <Badge
                              key={ct.id}
                              variant={siteForm.preferred_compliance_type_ids.includes(ct.id) ? 'default' : 'outline'}
                              className="cursor-pointer"
                              onClick={() => toggleComplianceType(ct.id, 'preferred_compliance_type_ids')}
                            >
                              {siteForm.preferred_compliance_type_ids.includes(ct.id) && (
                                <Star className="h-3 w-3 mr-1" />
                              )}
                              {ct.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  {/* Allowance Rates */}
                  {allowanceTypes.filter(a => a.is_active !== false).length > 0 && (
                    <div className="space-y-3">
                      <Label className="label-overline flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        Allowance Rates
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Set per-site allowance amounts. Payroll rules with site scope use these rates.
                      </p>
                      <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                        {allowanceTypes.filter(a => a.is_active !== false).map((at) => {
                          const existing = (siteForm.allowance_rates || []).find(r => r.allowance_type_id === at.id);
                          return (
                            <div key={at.id} className="flex items-center justify-between gap-3 py-1" data-testid={`site-allowance-rate-${at.id}`}>
                              <span className="text-sm flex-1">{at.name}</span>
                              <div className="flex items-center gap-1.5 w-32">
                                <span className="text-muted-foreground text-sm">$</span>
                                <Input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  className="h-8 text-sm"
                                  placeholder="—"
                                  value={existing?.amount ?? ''}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    const rates = [...(siteForm.allowance_rates || [])].filter(r => r.allowance_type_id !== at.id);
                                    if (val !== '' && parseFloat(val) > 0) {
                                      rates.push({ allowance_type_id: at.id, amount: parseFloat(val) });
                                    }
                                    setSiteForm({ ...siteForm, allowance_rates: rates });
                                  }}
                                  data-testid={`site-allowance-amount-${at.id}`}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="label-overline">Notes</Label>
                    <Textarea
                      data-testid="site-notes-input"
                      value={siteForm.notes}
                      onChange={(e) => setSiteForm({ ...siteForm, notes: e.target.value })}
                      placeholder="Site access instructions, contact details..."
                      rows={3}
                    />
                  </div>
                  <div className="flex gap-2 pt-4 border-t mt-6">
                    <Button type="button" variant="outline" onClick={() => setSiteDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="btn-volt" data-testid="save-site-btn">
                      Update Site
                    </Button>
                  </div>
                </form>
              </TabsContent>

              {/* Contacts Tab */}
              <TabsContent value="contacts" className="mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Site Contacts ({siteContacts.length})
                  </h3>
                  <div className="flex gap-2">
                    {availableCustomerContacts.length > 0 && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setLinkContactDialogOpen(true)}
                        data-testid="link-contact-btn"
                      >
                        <Link2 className="h-4 w-4 mr-1" />
                        Link Existing
                      </Button>
                    )}
                    <Button 
                      className="btn-volt" 
                      size="sm"
                      onClick={() => handleOpenSiteContactDialog()}
                      data-testid="add-site-contact-btn"
                    >
                      <UserPlus className="h-4 w-4 mr-1" />
                      Add New
                    </Button>
                  </div>
                </div>

                {siteContacts.length === 0 ? (
                  <div className="text-center py-8 bg-muted/50 rounded-lg">
                    <Users className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground mb-3">No contacts added to this site yet</p>
                    {availableCustomerContacts.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        You can link existing customer contacts or add new ones
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {siteContacts.map(contact => (
                      <div 
                        key={contact.id} 
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          contact.is_primary ? 'bg-primary/10 border border-primary/30' : 'bg-muted/50'
                        }`}
                        data-testid={`site-contact-${contact.id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{contact.name}</span>
                            {contact.is_primary && (
                              <Badge className="text-xs bg-primary text-primary-foreground">
                                <UserCheck className="h-3 w-3 mr-1" />
                                Primary
                              </Badge>
                            )}
                            {contact.customer_contact_id && (
                              <Badge variant="outline" className="text-xs">
                                <Link2 className="h-3 w-3 mr-1" />
                                Linked
                              </Badge>
                            )}
                            {contact.job_title && (
                              <Badge variant="outline" className="text-xs">{contact.job_title}</Badge>
                            )}
                          </div>
                          <div className="flex gap-4 text-sm text-muted-foreground mt-1">
                            {contact.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {contact.phone}
                              </span>
                            )}
                            {contact.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="h-3 w-3" />
                                {contact.email}
                              </span>
                            )}
                          </div>
                          {contact.notes && (
                            <p className="text-xs text-muted-foreground mt-1">{contact.notes}</p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleOpenSiteContactDialog(contact)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteSiteContact(contact.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-2 pt-4 border-t mt-6">
                  <Button variant="outline" onClick={() => setSiteDialogOpen(false)}>Close</Button>
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            /* New Site Form (no tabs) */
            <form onSubmit={handleSiteSubmit} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="label-overline">Customer <span className="text-red-500">*</span></Label>
                <AsyncCustomerPicker
                  value={siteForm.customer_id}
                  onChange={(id) => setSiteForm({ ...siteForm, customer_id: id })}
                  testId="site-customer-select"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Search Address</Label>
                <GooglePlacesAutocomplete
                  placeholder="Start typing an address..."
                  country={tenantCountry}
                  onPlaceSelect={(place) => {
                    setSiteForm(prev => ({
                      ...prev,
                      name: [place.street_address, place.suburb].filter(Boolean).join(', ') || place.name || prev.name,
                      street_address: place.street_address || '',
                      suburb: place.suburb || '',
                      state: place.state || '',
                      postcode: place.postcode || ''
                    }));
                  }}
                  data-testid="site-address-autocomplete"
                />
                <p className="text-xs text-muted-foreground">Or enter address manually below</p>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Site Name</Label>
                <Input
                  data-testid="site-name-input"
                  value={siteForm.name}
                  onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })}
                  placeholder="e.g. 17 Highview Cres, Abuntha"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Street Address</Label>
                <Input
                  data-testid="site-street-input"
                  value={siteForm.street_address}
                  onChange={(e) => setSiteForm({ ...siteForm, street_address: e.target.value })}
                  placeholder="123 Main Street"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-overline">Suburb</Label>
                  <Input
                    data-testid="site-suburb-input"
                    value={siteForm.suburb}
                    onChange={(e) => setSiteForm({ ...siteForm, suburb: e.target.value })}
                    placeholder="Sydney"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-overline">Postcode</Label>
                  <Input
                    data-testid="site-postcode-input"
                    value={siteForm.postcode}
                    onChange={(e) => setSiteForm({ ...siteForm, postcode: e.target.value })}
                    placeholder="2000"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">State</Label>
                <Select 
                  value={siteForm.state} 
                  onValueChange={(v) => setSiteForm({ ...siteForm, state: v })}
                >
                  <SelectTrigger data-testid="site-state-select">
                    <SelectValue placeholder={`Select ${countryMeta?.stateLabel?.toLowerCase() || 'state'}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {statesList.map(s => (
                      <SelectItem key={s.code} value={s.code}>{s.code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Allowance Rates */}
              {allowanceTypes.filter(a => a.is_active !== false).length > 0 && (
                <div className="space-y-3">
                  <Label className="label-overline flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    Allowance Rates
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Set per-site allowance amounts. Payroll rules with site scope use these rates.
                  </p>
                  <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                    {allowanceTypes.filter(a => a.is_active !== false).map((at) => {
                      const existing = (siteForm.allowance_rates || []).find(r => r.allowance_type_id === at.id);
                      return (
                        <div key={at.id} className="flex items-center justify-between gap-3 py-1" data-testid={`new-site-allowance-rate-${at.id}`}>
                          <span className="text-sm flex-1">{at.name}</span>
                          <div className="flex items-center gap-1.5 w-32">
                            <span className="text-muted-foreground text-sm">$</span>
                            <Input
                              type="number"
                              min={0}
                              step={0.01}
                              className="h-8 text-sm"
                              placeholder="—"
                              value={existing?.amount ?? ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                const rates = [...(siteForm.allowance_rates || [])].filter(r => r.allowance_type_id !== at.id);
                                if (val !== '' && parseFloat(val) > 0) {
                                  rates.push({ allowance_type_id: at.id, amount: parseFloat(val) });
                                }
                                setSiteForm({ ...siteForm, allowance_rates: rates });
                              }}
                              data-testid={`new-site-allowance-amount-${at.id}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label className="label-overline">Notes</Label>
                <Textarea
                  data-testid="site-notes-input"
                  value={siteForm.notes}
                  onChange={(e) => setSiteForm({ ...siteForm, notes: e.target.value })}
                  placeholder="Site access instructions, contact details..."
                  rows={3}
                />
              </div>
              <SheetFooter className="pt-4 border-t mt-6">
                <Button type="button" variant="outline" onClick={() => setSiteDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="btn-volt" data-testid="save-site-btn">
                  Add Site
                </Button>
              </SheetFooter>
            </form>
          )}
        </SheetContent>
      </Sheet>

      {/* Site Contact Dialog */}
      <Dialog open={siteContactDialogOpen} onOpenChange={setSiteContactDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingSiteContact ? 'Edit Site Contact' : 'Add New Site Contact'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSiteContactSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">Name <span className="text-red-500">*</span></Label>
              <Input
                value={siteContactForm.name}
                onChange={(e) => setSiteContactForm({ ...siteContactForm, name: e.target.value })}
                placeholder="Contact name"
                required
                data-testid="site-contact-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Job Title / Role</Label>
              <Input
                value={siteContactForm.job_title}
                onChange={(e) => setSiteContactForm({ ...siteContactForm, job_title: e.target.value })}
                placeholder="e.g. Site Manager, Foreman"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Phone</Label>
                <Input
                  value={siteContactForm.phone}
                  onChange={(e) => setSiteContactForm({ ...siteContactForm, phone: e.target.value })}
                  placeholder="+61 400 000 000"
                />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Email</Label>
                <Input
                  type="email"
                  value={siteContactForm.email}
                  onChange={(e) => setSiteContactForm({ ...siteContactForm, email: e.target.value })}
                  placeholder="contact@company.com"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="label-overline">Notes</Label>
              <Textarea
                value={siteContactForm.notes}
                onChange={(e) => setSiteContactForm({ ...siteContactForm, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>

            <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
              <Checkbox
                id="site-contact-is-primary"
                checked={siteContactForm.is_primary}
                onCheckedChange={(checked) => setSiteContactForm({ ...siteContactForm, is_primary: checked })}
                data-testid="site-contact-is-primary-checkbox"
              />
              <div className="space-y-1">
                <Label htmlFor="site-contact-is-primary" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                  <UserCheck className="h-4 w-4" />
                  Set as Primary Site Contact
                </Label>
                <p className="text-xs text-muted-foreground">
                  Primary contact will be the main point of reference for this site
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSiteContactDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt" data-testid="site-contact-submit-btn">
                {editingSiteContact ? 'Update' : 'Add'} Contact
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Link Existing Customer Contact Dialog */}
      <Dialog open={linkContactDialogOpen} onOpenChange={setLinkContactDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Link Customer Contact
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Select a contact from {editingSite && (editingSite.customer_name || 'this customer')} to link to this site.
            </p>
            {availableCustomerContacts.length === 0 ? (
              <div className="text-center py-6 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  All customer contacts are already linked to this site
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {availableCustomerContacts.map(contact => (
                  <div 
                    key={contact.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{contact.name}</span>
                        {contact.is_primary && (
                          <Badge variant="outline" className="text-xs">Primary</Badge>
                        )}
                        {contact.position && (
                          <Badge variant="outline" className="text-xs">{contact.position}</Badge>
                        )}
                      </div>
                      <div className="flex gap-3 text-sm text-muted-foreground mt-1">
                        {contact.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {contact.phone}
                          </span>
                        )}
                        {contact.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {contact.email}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="btn-volt"
                      onClick={() => handleLinkCustomerContact(contact.id)}
                      data-testid={`link-customer-contact-${contact.id}`}
                    >
                      <Link2 className="h-4 w-4 mr-1" />
                      Link
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkContactDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive/Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => { setDeleteDialogOpen(open); if (!open) deletePendingRef.current = false; }}
        title={getDeleteDialogInfo().title}
        description={getDeleteDialogInfo().description}
        confirmText={deleteType === 'customer' || deleteType === 'site' ? 'Archive' : 'Delete'}
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        testId="delete-confirmation-dialog"
      />
    </div>
  );
};

export default Customers;
